import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ClockInControls, Widget, WidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink as ExternalLinkIcon } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  NotificationsInboxNotification,
  NotificationsSubscriptionDocument,
} from 'generated/resources/graphql'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import { buildNoQueryWidget } from 'modules/core/lib/widgets'

import ClockInChecker, {
  RenderProps as ClockInCheckerRenderProps,
} from 'modules/attendance/components/ClockIn/ClockInChecker'
import useMissingBreakWidget from 'modules/attendance/components/ClockIn/ClockInChecker/MissingBreakWidget/useMissingBreakWidget'
import { ProjectSelector } from 'modules/attendance/components/ClockIn/Tracker/Clockin/Options/ProjectSelector'
import { ProjectClockInData } from 'modules/attendance/components/ClockIn/Tracker/tracker'
import ClockOutInconsistenciesAlert from 'modules/attendance/components/TimeTracking/Modals/RestTimeAlertsAndReminders/clockOutInconsistenciesAlert'
import {
  AttendanceFeaturesProvider,
  useAttendanceFeatures,
} from 'modules/attendance/lib/attendanceFeaturesContext'
import OpenShiftUtils from 'modules/attendance/lib/utils/OpenShiftUtils'
import { ErrorTypes } from 'modules/attendance/lib/validateShiftBreakDurationRules'
import { ActionTypes } from 'modules/attendance/store/models/Shift'
import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'

import { getLocationTypes } from './constants'
import { getNormalizedData, performBreakStartAction, performClockInAction } from './helpers'

const APP_STORE_URL = 'https://apps.apple.com/es/app/factorial/id1479184236'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.factorialhr.factorialapp'
const NOTIFICATION_NAME = 'Attendance::TakeBreakNotifier'

const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.clock_in.title')

  const linkParams = {
    periodType: 'monthly',
    year: moment().year(),
    month: moment().month() + 1,
    day: moment().date(),
  }

  const { navigation } = useNavigation()

  const linkUrl = navigation.clockIn.periodTypeYearMonthDay.fullPath(linkParams)

  const handleUpperRightActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.CLOCK_IN,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
  }

  const link = {
    url: linkUrl,
    title,
    onClick: handleUpperRightActionClick,
  }

  return {
    link,
    linkParams,
    title,
  }
}

const getLabels = () => ({
  clockedIn: i18n.t('insights.widgets.status.clocked_in'),
  clockedOut: i18n.t('insights.widgets.status.clocked_out'),
  onBreak: i18n.t('insights.widgets.status.in_a_break'),
  resume: i18n.t('insights.widgets.status.resume'),
  remainingTime: i18n.t('insights.widgets.status.remaining_time'),
  overtime: i18n.t('insights.widgets.status.overtime'),
  clockIn: i18n.t('insights.widgets.status.clock_in'),
  clockOut: i18n.t('insights.widgets.status.clock_out'),
  break: i18n.t('insights.widgets.status.break'),
  selectLocation: i18n.t('insights.widgets.status.select_location'),
  selectProject: i18n.t('insights.widgets.status.select_project'),
  paid: i18n.t('insights.widgets.status.paid'),
  unpaid: i18n.t('insights.widgets.status.unpaid'),
})

type Props = {
  hasAutomaticBreaks: boolean
} & Pick<
  ClockInCheckerRenderProps,
  | 'onToggle'
  | 'projectClockInData'
  | 'hasAutomaticBreaks'
  | 'employeeMissingClockOut'
  | 'restTimeAlertInconsistencies'
  | 'hasRestTime'
  | 'todayShifts'
  | 'lastWorkingShift'
  | 'trackedDurationToday'
  | 'maxRegularMinutes'
  | 'hasProjectEnabled'
  | 'openShift'
  | 'loading'
  | 'breaksEnabled'
  | 'mobileClockIn'
  | 'desktopClockIn'
  | 'breakType'
  | 'breakTypes'
  | 'breakConfigurations'
  | 'canSeeOwnedAttendance'
  | 'enableClockInLocation'
  | 'breakSetting'
>

const MobileClockInWidgetComponent: FC = () => {
  const { link, title } = useLinkAndTitle()

  const onClickiOS = () => {
    window.open(APP_STORE_URL, '_blank')
  }

  const onClickAndroid = () => {
    window.open(PLAY_STORE_URL, '_blank')
  }

  const actions = [
    {
      label: 'iOS',
      icon: ExternalLinkIcon,
      onClick: onClickiOS,
      variant: 'outline' as const,
    },
    {
      label: 'Android',
      icon: ExternalLinkIcon,
      onClick: onClickAndroid,
      variant: 'outline' as const,
    },
  ]

  return (
    <Widget
      header={{
        title,
        link,
      }}
    >
      <Box id="mobile-clock-in-widget" flexGrow>
        <WidgetEmptyState
          title={i18n.t('insights.widgets.status.mobile.title')}
          description={i18n.t('insights.widgets.status.mobile.description')}
          emoji="📱"
          actions={actions}
        />
      </Box>
    </Widget>
  )
}

const ClockInWidgetComponent: FC<Props> = ({
  onToggle,
  hasAutomaticBreaks,
  projectClockInData: lastProjectData,
  employeeMissingClockOut,
  restTimeAlertInconsistencies,
  hasRestTime,
  hasProjectEnabled,
  maxRegularMinutes: estimatedMinutesToDo,
  trackedDurationToday,
  todayShifts,
  openShift,
  loading,
  breaksEnabled,
  mobileClockIn,
  desktopClockIn,
  breakType,
  breakTypes,
  breakConfigurations,
  canSeeOwnedAttendance,
  enableClockInLocation,
  breakSetting,
}) => {
  const { title, link, linkParams } = useLinkAndTitle()

  const { data: notificationData } = useSubscription(NotificationsSubscriptionDocument)
  const notification = notificationData?.notificationsInboxNotificationCreate?.inboxNotification

  // this is here to avoid to show the skeleton to be shown every time the user performs an action
  const [finalLoading, setFinalLoading] = useState(loading)

  useEffect(() => {
    if (!loading) {
      setFinalLoading(false)
    }
  }, [loading])

  const attendanceShifts = useMemo(
    () => uniqBy([...todayShifts, ...(openShift ? [openShift] : [])], 'id'),
    [todayShifts, openShift]
  )

  const locationTypes = getLocationTypes()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [projectData, setProjectData] = useState<ProjectClockInData>(lastProjectData || {})

  useEffect(() => {
    setProjectData((project) => (!project?.projectWorkerId ? lastProjectData : project))
  }, [lastProjectData])

  const [remainingMinutes, setRemainingMinutes] = useState(estimatedMinutesToDo)

  const { clockInLocationType, controlsData } = useMemo(
    () => getNormalizedData(attendanceShifts, employeeMissingClockOut),
    [attendanceShifts, employeeMissingClockOut]
  )

  const [locationTypeId, setLocationTypeId] = useState<string | undefined>(clockInLocationType)

  useEffect(() => {
    setLocationTypeId(clockInLocationType)
  }, [clockInLocationType])

  const openShiftId = openShift?.id

  const { hideForgotClockOutWidget, hideTakeBreakWidget, showMissingBreakWidget } =
    LOCAL_STORAGE_KEYS
  const [hideWidget, setHideWidget] = useLocalStorage<string>(hideForgotClockOutWidget, '')

  const [takeBreakWidgetNotification, setTakeBreakWidgetNotification] = useLocalStorage(
    hideTakeBreakWidget,
    ''
  )
  const [activeNotif, setActiveNotif] = useState<NotificationsInboxNotification | null>(null)

  const [showClockOutInconsistenciesAlert, setShowClockOutInconsistenciesAlert] = useState(false)

  const { featureAvailability: hasCorrectiveBreaksFeature } = useAttendanceFeatures(
    'time_tracking_corrective_breaks'
  )
  const [breakAlertType, setBreakAlertType] = useLocalStorage<ErrorTypes | null>(
    showMissingBreakWidget,
    null
  )
  const openShiftLastClockInDate = openShift
    ? OpenShiftUtils.openShiftLastClockInDate(openShift)
    : undefined

  const displayMissingClockout =
    employeeMissingClockOut && hideWidget !== `${hideForgotClockOutWidget}:${openShiftId}`

  const ignoreMissingClockout = () => {
    setHideWidget(`${hideForgotClockOutWidget}:${openShiftId}`)
  }

  useEffect(() => {
    if (takeBreakWidgetNotification) {
      setActiveNotif(JSON.parse(takeBreakWidgetNotification))
    }
  }, [])

  useEffect(() => {
    if (notification && notification.notifier === NOTIFICATION_NAME) {
      const stored = takeBreakWidgetNotification

      if (!stored || JSON.parse(stored).id !== notification.id) {
        setTakeBreakWidgetNotification(JSON.stringify({ ...notification }))
        setActiveNotif(notification)
      }
    }
  }, [notification?.id, takeBreakWidgetNotification])

  const ignoreTakeBreakWidget = () => {
    localStorage.removeItem(hideTakeBreakWidget)
    setActiveNotif(null)
  }

  const [data, setData] = useState(controlsData)

  useEffect(() => {
    setData(controlsData)
  }, [controlsData])

  const lastControlShift = data.at(-1)

  const clockedIn = lastControlShift?.variant === 'clocked-in' && lastControlShift.inProgress
  const clockedOut = lastControlShift?.variant === 'clocked-out' || !lastControlShift
  const duringBreak = lastControlShift?.variant === 'break' && lastControlShift.inProgress

  const updateDataEveryMinute = useCallback(() => {
    setRemainingMinutes(estimatedMinutesToDo - trackedDurationToday)
    setData((oldData) => {
      const lastControl = oldData.at(-1)

      if (!lastControl || lastControl.variant === 'clocked-out') {
        return oldData
      }

      return [
        ...oldData.slice(0, -1),
        {
          ...lastControl,
          to: new Date(),
        },
      ]
    })
  }, [estimatedMinutesToDo, trackedDurationToday])

  useEffect(() => {
    updateDataEveryMinute()
  }, [updateDataEveryMinute])

  const resolveMissingClockout = () => {
    navigate(navigation.clockIn.periodTypeYearMonthDay.fullPath(linkParams))
  }

  const onClockIn = async () => {
    if (duringBreak) {
      await onToggle(ActionTypes.BREAK_END_ACTION, locationTypeId, breakType, projectData)
      checkForBreakAlert(ErrorTypes.BREAK_MINIMUM)
    } else {
      await onToggle(ActionTypes.CLOCK_IN_ACTION, locationTypeId, undefined, projectData)
    }
    setData(performClockInAction(data))
  }

  const onClockOut = async () => {
    const shouldShowClockOutInconsistenciesAlert =
      hasRestTime && !!restTimeAlertInconsistencies?.length
    if (shouldShowClockOutInconsistenciesAlert) {
      setShowClockOutInconsistenciesAlert(shouldShowClockOutInconsistenciesAlert)
      return
    }

    await onToggle(ActionTypes.CLOCK_OUT_ACTION, locationTypeId)
    checkForBreakAlert(ErrorTypes.REST_TIME)
  }

  const onClockOutAndFix = () => {
    onClockOut()
    navigate(navigation.clockIn.fullPath())
  }

  const onBreak = async (breakTypeId?: string) => {
    const breakConfiguration = breakTypeId
      ? breakConfigurations.find(
          (config) => `${config.timeSettingsBreakConfigurationId}` === breakTypeId
        )
      : undefined
    await onToggle(ActionTypes.BREAK_START_ACTION, locationTypeId, breakConfiguration)
    setData(performBreakStartAction(data))
  }

  const checkForBreakAlert = (alertType: ErrorTypes) => {
    if (hasRestTime && hasCorrectiveBreaksFeature) {
      setBreakAlertType(alertType)
    } else {
      const isBreakAlertStored = !!breakAlertType
      if (isBreakAlertStored) {
        onDismissBreakAlert()
      }
    }
  }

  const onDismissBreakAlert = () => {
    setBreakAlertType(null)
    localStorage.removeItem(showMissingBreakWidget)
  }
  const onReviewBreakAlert = () => {
    onDismissBreakAlert()
    navigate(navigation.clockIn.fullPath())
  }

  const handleChangeBreakTypeId = () => {}

  const canSeeGraphAndRemainingTime = canSeeOwnedAttendance

  const canShowBreakButton = !hasAutomaticBreaks && !!breaksEnabled

  const missingBreakAlertNode = useMissingBreakWidget({
    breakSetting,
    todayShifts,
    hasCorrectiveBreaksFeature,
    alertTypeToCheck: breakAlertType,
    onDismiss: onDismissBreakAlert,
    onReview: onReviewBreakAlert,
  })
  const displayMissingBreakWidget =
    !!missingBreakAlertNode && !activeNotif && !displayMissingClockout

  if (mobileClockIn && !desktopClockIn) {
    return <MobileClockInWidgetComponent />
  }

  if (finalLoading) {
    return <Widget.Skeleton height="lg" />
  }

  return (
    <Widget
      header={{
        title,
        link,
      }}
    >
      <Box flexDirection="column" justifyContent="center" id="clock-in-widget" flexGrow>
        {displayMissingBreakWidget && missingBreakAlertNode}
        {displayMissingClockout && (
          <WidgetEmptyState
            title={i18n.t('insights.widgets.status.forgot_clock_out.title')}
            description={i18n.t('insights.widgets.status.forgot_clock_out.description', {
              date: openShiftLastClockInDate,
            })}
            actions={[
              {
                label: i18n.t('insights.widgets.status.forgot_clock_out.resolve'),
                onClick: resolveMissingClockout,
              },
              openShiftId && {
                label: i18n.t('insights.widgets.status.forgot_clock_out.ignore'),
                variant: 'outline',
                onClick: ignoreMissingClockout,
              },
            ].filter(Boolean)}
          />
        )}
        {activeNotif && (
          <WidgetEmptyState
            title={i18n.t('clock_in.widget.take_break.title')}
            description={activeNotif.body}
            emoji="☕️"
            actions={[
              {
                label: i18n.t('clock_in.widget.take_break.cta'),
                variant: 'outline',
                onClick: () => {
                  onBreak()
                  ignoreTakeBreakWidget()
                },
              },
              {
                label: i18n.t('clock_in.widget.take_break.ignore'),
                onClick: ignoreTakeBreakWidget,
              },
            ]}
          />
        )}
        {showClockOutInconsistenciesAlert && (
          <ClockOutInconsistenciesAlert
            onClose={() => setShowClockOutInconsistenciesAlert(false)}
            onClockOut={onClockOut}
            onClockOutAndFix={onClockOutAndFix}
            inconsistencyErrors={restTimeAlertInconsistencies}
          />
        )}
        {!displayMissingClockout && !activeNotif && !displayMissingBreakWidget && (
          <ClockInControls
            labels={getLabels()}
            trackedMinutes={trackedDurationToday}
            breakTypes={breakTypes}
            onChangeBreakTypeId={handleChangeBreakTypeId}
            data={data}
            locationId={locationTypeId}
            remainingMinutes={remainingMinutes}
            onClockIn={onClockIn}
            onClockOut={onClockOut}
            onBreak={onBreak}
            canShowBreakButton={canShowBreakButton}
            canShowLocation={enableClockInLocation}
            locations={locationTypes}
            onChangeLocationId={setLocationTypeId}
            canShowProject={hasProjectEnabled}
            canSeeGraph={canSeeGraphAndRemainingTime}
            canSeeRemainingTime={canSeeGraphAndRemainingTime}
            breakTypeName={breakType?.name}
            projectSelectorElement={
              <ProjectSelector
                clockedIn={clockedIn}
                disabled={!clockedOut}
                projectData={projectData}
                setProjectData={setProjectData}
                factorialOneVersion
              />
            }
          />
        )}
      </Box>
    </Widget>
  )
}

const WrappedComponent = () => (
  <AttendanceFeaturesProvider>
    <ClockInChecker>
      {({
        onToggle,
        hasAutomaticBreaks,
        employeeMissingClockOut,
        restTimeAlertInconsistencies,
        hasRestTime,
        lastWorkingShift,
        openShift,
        todayShifts,
        trackedDurationToday,
        projectClockInData,
        maxRegularMinutes,
        hasProjectEnabled,
        loading,
        breaksEnabled,
        mobileClockIn,
        desktopClockIn,
        breakType,
        breakTypes,
        breakConfigurations,
        canSeeOwnedAttendance,
        enableClockInLocation,
        breakSetting,
      }: ClockInCheckerRenderProps) => {
        return (
          <ClockInWidgetComponent
            onToggle={onToggle}
            hasAutomaticBreaks={hasAutomaticBreaks}
            employeeMissingClockOut={employeeMissingClockOut}
            restTimeAlertInconsistencies={restTimeAlertInconsistencies}
            hasRestTime={hasRestTime}
            projectClockInData={projectClockInData}
            openShift={openShift}
            lastWorkingShift={lastWorkingShift}
            todayShifts={todayShifts}
            trackedDurationToday={trackedDurationToday}
            maxRegularMinutes={maxRegularMinutes}
            hasProjectEnabled={hasProjectEnabled}
            loading={loading}
            breaksEnabled={breaksEnabled}
            mobileClockIn={mobileClockIn}
            desktopClockIn={desktopClockIn}
            breakType={breakType}
            breakTypes={breakTypes}
            breakConfigurations={breakConfigurations}
            canSeeOwnedAttendance={canSeeOwnedAttendance}
            enableClockInLocation={enableClockInLocation}
            breakSetting={breakSetting}
          />
        )
      }}
    </ClockInChecker>
  </AttendanceFeaturesProvider>
)

export const buildClockInWidget = () =>
  buildNoQueryWidget({
    id: 'clock-in',
    component: WrappedComponent,
  })
